import { useField } from 'formik';
import { ReactElement, useEffect, useMemo } from 'react';
import { getLocalizedText } from '../../../../../../i18n/i18n.utils';
import { PickerInput } from '../../../../shared/component/widget/picker-input';
import { PickerOption } from '../../../../shared/component/widget/widget.types';
import {
  ApplicationDefaultParameterSetFormTags,
  ApplicationDefaultParameterSetTag,
} from '../../../../shared/model/flow.types';
import { getAvailableApplicationParameterSetTagOptions } from '../../../../shared/util/flow-item.util';
import { BeaconWeldingData } from '../../model/beacon-welding-data';

interface WeldParameterSetTagsPicklistItemProps {
  process: BeaconWeldingData.WeldProcess | undefined;
  tagName: string;
  tagData: ApplicationDefaultParameterSetTag;
  tags: ApplicationDefaultParameterSetFormTags | undefined;
  readOnly?: boolean;
}

const WeldApplicationTagPicker = (
  props: WeldParameterSetTagsPicklistItemProps
): ReactElement => {
  const { process, tagData, tagName, tags, readOnly } = props;

  const fieldName = `tags.${tagName}`;

  const [field, , helper] = useField(fieldName);

  const value = tags ? tags[tagName] : undefined;

  let isDisabled = false;

  const validOptions = useMemo(() => {
    if (tagData.dependency === undefined) {
      return tagData.options;
    }

    return isDisabled || process === undefined
      ? []
      : getAvailableApplicationParameterSetTagOptions(tagData.options, {
          ...tags,
          process,
        });
  }, [isDisabled, process, tagData.dependency, tagData.options, tags]);

  const selectOptions: PickerOption[] = validOptions.map((option) => {
    return {
      label: option.label,
      value: option.value,
    };
  });

  // determine whether to disable the tags
  if (tagData.dependency !== undefined) {
    if (tags === undefined) {
      isDisabled = true;
    } else if (
      tagData.dependency === BeaconWeldingData.FlowItemParameterRef.PROCESS
    ) {
      isDisabled = !process;
    } else {
      isDisabled =
        !(tagData.dependency in tags) ||
        tags[tagData.dependency] === undefined ||
        selectOptions.length === 0;
    }
  }

  // reset values with dependencies when other values change
  useEffect(() => {
    if (tagData.dependency === undefined) {
      return;
    }

    const valueIsValid =
      validOptions.find((opt) => opt.value === field.value) !== undefined;

    if (
      (isDisabled && field.value !== undefined) ||
      (field.value !== undefined && !valueIsValid)
    ) {
      helper.setValue(undefined);
    }
  }, [
    field.name,
    field.value,
    helper,
    isDisabled,
    tagData.dependency,
    tags,
    validOptions,
  ]);

  return (
    <PickerInput
      name={fieldName}
      value={value}
      options={selectOptions}
      label={getLocalizedText(tagData.label)}
      disabled={isDisabled || readOnly}
      placeholder={getLocalizedText('welding:make_selection')}
    />
  );
};

export { WeldApplicationTagPicker };
